import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { LanguageProvider } from './context/LanguageContext';
import setupAxiosInterceptors from './utils/axiosInterceptor';
import Registration from './pages/Registration';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import StockManagement from './pages/StockManagement';
import GRNEntry from './pages/GRNEntry';
import POSBilling from './pages/POSBilling';
import BillsHistory from './pages/BillsHistory';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import CashRegister from './pages/CashRegister';
import Expenses from './pages/Expenses';
import CustomerLedger from './pages/CustomerLedger';
import SupplierLedger from './pages/SupplierLedger';
import RawMaterialUsage from './pages/RawMaterialUsage';
import RawMaterialHistory from './pages/RawMaterialHistory';
import RawMaterialMovement from './pages/RawMaterialMovement';
import StockPriceManagement from './pages/StockPriceManagement';
import PaymentCollection from './pages/PaymentCollection';
import PaymentApprovals from './pages/PaymentApprovals';
import PaymentCollectionHistory from './pages/PaymentCollectionHistory';
import Layout from './components/Layout';
import SubscriptionGate from './components/SubscriptionGate';

// Settings Sub-pages
import {
  BusinessSettings,
  TaxSettings,
  CategorySettings,
  BrandSettings,
  SupplierSettings,
  CustomerSettings,
  CashierSettings,
  BillSettings,
  UnitSettings
} from './pages/settings';
import LanguageSettings from './pages/settings/LanguageSettings';
import ExpenseCategoriesSettings from './pages/settings/ExpenseCategoriesSettings';
import SubscriptionSettings from './pages/settings/SubscriptionSettings';
import CustomerCustomPrices from './pages/settings/CustomerCustomPrices';
import ResetBusinessSettings from './pages/settings/ResetBusinessSettings';
import DamagedStock from './pages/DamagedStock';
import SalesReturn from './pages/SalesReturn';
import GRNPaymentHistory from './pages/GRNPaymentHistory';
import GRNReturn from './pages/GRNReturn';
import BarcodeGenerator from './pages/BarcodeGenerator';
import ProfitAnalysis from './pages/ProfitAnalysis';

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('pos_token');
  if (!token) {
    return <Navigate to="/gposp/login" replace />;
  }
  return (
    <SubscriptionGate>
      <Layout>{children}</Layout>
    </SubscriptionGate>
  );
};

const POSApp = () => {
  // Setup axios interceptors on mount
  useEffect(() => {
    setupAxiosInterceptors();
  }, []);

  return (
    <LanguageProvider>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/register" element={<Registration />} />
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
        <Route path="/stock-management" element={<ProtectedRoute><StockManagement /></ProtectedRoute>} />
        <Route path="/grn-entry" element={<ProtectedRoute><GRNEntry /></ProtectedRoute>} />
        <Route path="/pos" element={<ProtectedRoute><POSBilling /></ProtectedRoute>} />
        <Route path="/bills-history" element={<ProtectedRoute><BillsHistory /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
        <Route path="/cash-register" element={<ProtectedRoute><CashRegister /></ProtectedRoute>} />
        <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
        <Route path="/customer-ledger" element={<ProtectedRoute><CustomerLedger /></ProtectedRoute>} />
        <Route path="/supplier-ledger" element={<ProtectedRoute><SupplierLedger /></ProtectedRoute>} />
        <Route path="/raw-material-usage" element={<ProtectedRoute><RawMaterialUsage /></ProtectedRoute>} />
        <Route path="/raw-material-history" element={<ProtectedRoute><RawMaterialHistory /></ProtectedRoute>} />
        <Route path="/raw-material-movement" element={<ProtectedRoute><RawMaterialMovement /></ProtectedRoute>} />
        <Route path="/stock-price-management" element={<ProtectedRoute><StockPriceManagement /></ProtectedRoute>} />
        <Route path="/payment-collection" element={<ProtectedRoute><PaymentCollection /></ProtectedRoute>} />
        <Route path="/payment-approvals" element={<ProtectedRoute><PaymentApprovals /></ProtectedRoute>} />
        <Route path="/payment-collection-history" element={<ProtectedRoute><PaymentCollectionHistory /></ProtectedRoute>} />
        <Route path="/damaged-stock" element={<ProtectedRoute><DamagedStock /></ProtectedRoute>} />
        <Route path="/sales-return" element={<ProtectedRoute><SalesReturn /></ProtectedRoute>} />
        <Route path="/grn-payment-history" element={<ProtectedRoute><GRNPaymentHistory /></ProtectedRoute>} />
        <Route path="/grn-return" element={<ProtectedRoute><GRNReturn /></ProtectedRoute>} />
        <Route path="/barcode-generator" element={<ProtectedRoute><BarcodeGenerator /></ProtectedRoute>} />
        <Route path="/profit-analysis" element={<ProtectedRoute><ProfitAnalysis /></ProtectedRoute>} />
        
        {/* Settings Routes */}
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/settings/business" element={<ProtectedRoute><BusinessSettings /></ProtectedRoute>} />
        <Route path="/settings/reset-business" element={<ProtectedRoute><ResetBusinessSettings /></ProtectedRoute>} />
        <Route path="/settings/tax" element={<ProtectedRoute><TaxSettings /></ProtectedRoute>} />
        <Route path="/settings/categories" element={<ProtectedRoute><CategorySettings /></ProtectedRoute>} />
        <Route path="/settings/brands" element={<ProtectedRoute><BrandSettings /></ProtectedRoute>} />
        <Route path="/settings/suppliers" element={<ProtectedRoute><SupplierSettings /></ProtectedRoute>} />
        <Route path="/settings/customers" element={<ProtectedRoute><CustomerSettings /></ProtectedRoute>} />
        <Route path="/settings/customers/:customerId/prices" element={<ProtectedRoute><CustomerCustomPrices /></ProtectedRoute>} />
        <Route path="/settings/cashiers" element={<ProtectedRoute><CashierSettings /></ProtectedRoute>} />
        <Route path="/settings/bill" element={<ProtectedRoute><BillSettings /></ProtectedRoute>} />
        <Route path="/settings/units" element={<ProtectedRoute><UnitSettings /></ProtectedRoute>} />
        <Route path="/settings/language" element={<ProtectedRoute><LanguageSettings /></ProtectedRoute>} />
        <Route path="/settings/expense-categories" element={<ProtectedRoute><ExpenseCategoriesSettings /></ProtectedRoute>} />
        <Route path="/settings/subscription" element={<ProtectedRoute><SubscriptionSettings /></ProtectedRoute>} />
        
        <Route path="/" element={<Navigate to="/gposp/login" replace />} />
      </Routes>
    </LanguageProvider>
  );
};

export default POSApp;
